import {
  BindingCandidate,
  ChannelType,
  ChannelUsageEntry,
  DataSourceDevice,
  MeasurementPoint,
  Source,
  UnboundChannel,
} from '@core/models/installation.model';
import { TagState } from '@custom-components';
import { channelKey } from '../../channels';
import { ROLE_GROUP_ICONS, roleGroupKey } from '../../role-groups';

export interface ChannelHolder {
  pointName: string;
  // Null when the holder is a point of this installation.
  installationName: string | null;
  icon: string | null;
}

export type ChannelStatus =
  | { kind: 'current' }
  | { kind: 'available' }
  // Held by another point; a candidate can still be chosen, which is what the refusal explains.
  | { kind: 'held'; holder: ChannelHolder | null }
  | { kind: 'unitMismatch' }
  | { kind: 'notForPoint' };

export interface ChannelRow {
  key: string;
  source: Source;
  unit: string | null;
  // Shown beside the value name only where the device has that value name more than once.
  context: string | null;
  candidate: BindingCandidate | null;
  selectable: boolean;
  status: ChannelStatus;
  sharedWith: string[];
  otherInstallations: string[];
}

export interface DeviceChannels {
  device: DataSourceDevice;
  rows: ChannelRow[];
  available: number;
}

export interface ChannelInputs {
  installationOid: string;
  point: MeasurementPoint | null;
  points: readonly MeasurementPoint[];
  devices: readonly DataSourceDevice[];
  candidates: readonly BindingCandidate[];
  unbound: readonly UnboundChannel[];
  usages: readonly ChannelUsageEntry[];
  channelTypes: readonly ChannelType[];
}

// The reader may spell a value name with a trailing qualifier; the vocabulary stores the base.
function baseValueName(valueName: string): string {
  return valueName.split(':', 1)[0];
}

export function rowKey(
  source: Pick<Source, 'deviceId' | 'contextType' | 'contextNumber' | 'valueName'>,
): string {
  return channelKey({ ...source, valueName: source.valueName && baseValueName(source.valueName) });
}

function typeKey(contextType: string | null, valueName: string | null): string {
  return `${contextType}/${valueName === null ? null : baseValueName(valueName)}`;
}

function unboundSource(channel: UnboundChannel): Source {
  return {
    kind: 'device_channel',
    deviceId: channel.deviceId,
    ldn: channel.ldn,
    serial: channel.serial,
    contextType: channel.contextType,
    contextNumber: channel.contextNumber,
    valueName: channel.valueName,
    label: channel.label,
  };
}

function roleIcon(role: string | null | undefined): string | null {
  return role ? ROLE_GROUP_ICONS[roleGroupKey(role)] : null;
}

interface Draft {
  source: Source;
  candidate: BindingCandidate | null;
  usage: ChannelUsageEntry | null;
  // A point of this installation that reads the channel.
  holderHere: MeasurementPoint | null;
}

// Every channel of every device here, from what fits the point (the candidates), what is free (the
// unbound channels) and what is held (the usage and this installation's own bindings).
export function deviceChannels(inputs: ChannelInputs): DeviceChannels[] {
  const { point, points, installationOid } = inputs;
  const units = new Map(
    inputs.channelTypes.map(type => [typeKey(type.contextType, type.valueName), type.unit]),
  );
  const currentKey = point?.binding ? rowKey(point.binding.source) : null;
  const drafts = new Map<string, Draft>();
  const draft = (source: Source): Draft => {
    const key = rowKey(source);
    let found = drafts.get(key);
    if (!found) {
      found = { source, candidate: null, usage: null, holderHere: null };
      drafts.set(key, found);
    }
    return found;
  };

  for (const candidate of inputs.candidates) {
    draft(candidate.source).candidate = candidate;
  }
  for (const usage of inputs.usages) {
    draft(usage.source).usage = usage;
  }
  for (const holder of points) {
    if (holder.binding?.source.deviceId != null) draft(holder.binding.source).holderHere = holder;
  }
  for (const channel of inputs.unbound) {
    draft(unboundSource(channel));
  }

  const holderOf = (found: Draft): ChannelHolder | null => {
    const byCandidate = found.candidate?.inUseBy ?? null;
    const pointOid = byCandidate?.pointOid ?? found.usage?.pointOid ?? found.holderHere?.pointOid;
    const elsewhereOid =
      byCandidate?.installationOid ?? found.usage?.installationOid ?? installationOid;
    const here = points.find(other => other.pointOid === pointOid) ?? found.holderHere;
    if (here && elsewhereOid === installationOid) {
      return { pointName: here.name, installationName: null, icon: roleIcon(here.role) };
    }
    if (byCandidate) {
      const role = found.usage?.pointOid === byCandidate.pointOid ? found.usage.role : null;
      return {
        pointName: byCandidate.pointName,
        installationName: byCandidate.installationName,
        icon: roleIcon(role),
      };
    }
    if (found.usage) {
      return {
        pointName: found.usage.pointName,
        installationName: found.usage.installationName,
        icon: roleIcon(found.usage.role),
      };
    }
    return null;
  };

  const statusOf = (key: string, found: Draft, unit: string | null): ChannelStatus => {
    if (key === currentKey) return { kind: 'current' };
    const held = found.candidate
      ? found.candidate.inUse
      : found.usage !== null || found.holderHere !== null;
    if (held) return { kind: 'held', holder: holderOf(found) };
    if (found.candidate) return { kind: 'available' };
    return unit !== null && point !== null && unit !== point.unit
      ? { kind: 'unitMismatch' }
      : { kind: 'notForPoint' };
  };

  const rows: ChannelRow[] = [...drafts.entries()].map(([key, found]) => {
    const unit = units.get(typeKey(found.source.contextType, found.source.valueName)) ?? null;
    const status = statusOf(key, found, unit);
    return {
      key,
      source: found.source,
      unit,
      context: null,
      candidate: found.candidate,
      selectable: found.candidate !== null && status.kind !== 'current',
      status,
      sharedWith: (found.candidate?.sharedWithPoints ?? []).map(
        shared => points.find(other => other.pointOid === shared.pointOid)?.name ?? shared.role,
      ),
      otherInstallations: (found.candidate?.otherInstallations ?? []).map(other => other.name),
    };
  });

  const candidateOrder = new Map(
    inputs.candidates.map((candidate, index) => [rowKey(candidate.source), index]),
  );
  const rank = (row: ChannelRow): number => {
    if (row.status.kind === 'current') {
      return 0;
    }
    return row.selectable ? 1 : 2;
  };
  rows.sort(
    (a, b) =>
      rank(a) - rank(b) ||
      (candidateOrder.get(a.key) ?? 0) - (candidateOrder.get(b.key) ?? 0) ||
      (a.source.contextType ?? '').localeCompare(b.source.contextType ?? '') ||
      (a.source.contextNumber ?? 0) - (b.source.contextNumber ?? 0) ||
      (a.source.valueName ?? '').localeCompare(b.source.valueName ?? ''),
  );

  return inputs.devices.map(device => {
    const own = rows.filter(row => row.source.deviceId === device.deviceId);
    const names = own.map(row => row.source.valueName);
    const withContext = own.map(row => ({
      ...row,
      context:
        names.filter(name => name === row.source.valueName).length > 1
          ? (row.source.label ?? [row.source.contextType, row.source.contextNumber].join(' '))
          : null,
    }));
    return {
      device,
      rows: withContext,
      available: withContext.filter(row => row.selectable).length,
    };
  });
}

export interface StatusTag {
  state: TagState;
  // A translation key and its parameters.
  key: string;
  params: Record<string, string>;
  icon: string | null;
}

const STATUS_KEY = 'installations.editor.binding.status.';

export function statusTag(row: ChannelRow): StatusTag {
  const { status } = row;
  switch (status.kind) {
    case 'current':
      return {
        state: 'info',
        key: 'installations.editor.candidate.current',
        params: {},
        icon: null,
      };
    case 'available':
      return { state: 'success', key: `${STATUS_KEY}available`, params: {}, icon: null };
    case 'unitMismatch':
      return { state: 'neutral', key: `${STATUS_KEY}unitMismatch`, params: {}, icon: null };
    case 'notForPoint':
      return { state: 'neutral', key: `${STATUS_KEY}notForPoint`, params: {}, icon: null };
    case 'held': {
      const state = row.selectable ? 'warning' : 'neutral';
      const { holder } = status;
      if (holder === null) {
        return { state, key: `${STATUS_KEY}linkedHidden`, params: {}, icon: null };
      }
      return holder.installationName === null
        ? {
            state,
            key: `${STATUS_KEY}linkedTo`,
            params: { point: holder.pointName },
            icon: holder.icon,
          }
        : {
            state,
            key: `${STATUS_KEY}linkedElsewhere`,
            params: { point: holder.pointName, installation: holder.installationName },
            icon: holder.icon,
          };
    }
  }
}
